import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { messageHandler } from "utils/messageHandler";
import { AGENTOFFICEPRODUCTADDEDIT_MESSAGES } from "utils/messages";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Brand, Category, Warehouse, Media } from "../types";
import { nameValidation, statusValidation } from "validationSchemas/common";
import SharedEditProductUI from "components/SignageOfficeAgentInventoryProduct";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { getAuthCookie } from "../utils/authCookies";
// LEGACY (unused): react-table + warehouse inputs were only used by the old hierarchy table
// import {
//     useReactTable,
//     getCoreRowModel,
//     getPaginationRowModel,
//     type ColumnDef,
//     type Table,
// } from "@tanstack/react-table";
// import { UIReactSelect, UIInput } from "components/WarehouseInputfields";
import { getImageSrc, useVariantImageUrlMap, buildLegacyUrl } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles, } from "components/Imageshelper";
import { useBrandCategorySelector } from "components/BrandCategorySelector";
// type LinkedInstaller = {
//     id: string;
//     name: string;
//     warehouse_info: WarehouseInfo;
// }

// type WarehouseInfo = {
//   id: string;
//   label: string;
//   bin_location: string;
//   linked_product_quantity: number;
// }
type SelectOption = { value: string; label: string };

type AgentOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

type OfficeOption = {
    value: string;
    label: string;
};

interface Installer {
    _id: string;
    supplier_id: string;
    name: string;
    email: string;
}

// interface FirmDetails {
//   companyID: string;
//   companyName: string;
// }


// LEGACY (unused): row shape of the old warehouse hierarchy table
// type WHRow = {
//     warehouse_id: string;
//     warehouse_name: string;
//     zone_id: string; zone_name: string;
//     rack_id: string; rack_name: string;
//     bin_id: string; bin_name: string;
//     available: number; on_hand: number; reserved: number;
// };




const toOptions = (list: Array<{ id?: string; name?: string }> = []): SelectOption[] =>
    (Array.isArray(list) ? list : [])?.map((x) => ({ value: x?.id || x?.name || "", label: x?.name || "" }));



const conditionOptions: SelectOption[] = [
    { value: "new", label: "New" },
    { value: "used_like_new", label: "Used - Like New" },
    { value: "used_good", label: "Used - Good" },
];


const statusOptions: SelectOption[] = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
];

type PreviewFile = { file: File; url: string };


const AddAgentOfficeProduct = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [formSubmitting, setFormSubmitting] = useState(false);
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName") || "";
    const navigate = useNavigate();
    const location = useLocation();

    // Determine the base route based on current location
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);
    const [parentSignageName, setParentSignageName] = useState<string>("");
    const effectiveSupplierId =
        (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";
    const effectiveSupplierName =
        (isSignageTeammember && parentSignageName ? parentSignageName : userName) || "";
    
    // Check if URL contains "brokerageAgent" or "brokerageOffice" to determine dropdown type
    const isBrokerageAgent = location.pathname.includes('/brokerageAgent/');
    const isBrokerageOffice = location.pathname.includes('/brokerageOffice/');
    const brokerageId = isBrokerageAgent || isBrokerageOffice ? id : null;
    const agentIdFromRoute = !isBrokerageAgent && !isBrokerageOffice ? id : null;
    
    const [selectedAgentOption, setSelectedAgentOption] = useState<AgentOption | null>(null);
    const [selectedOfficeOption, setSelectedOfficeOption] = useState<OfficeOption | null>(null);
    const [offices, setOffices] = useState<any[]>([]);
    const [installers, setInstallers] = useState<Installer[]>([]);
    
    // ✅ Compute the agent/entity ID to use for brand-category selector
    // When user selects office → pass the route id (brokerage ID)
    // When user selects agent → pass the selected agent ID
    // For regular agent route (non-brokerage) → use agentIdFromRoute
    const agentIdForBrandCategory = useMemo(() => {
        // If it's a brokerage office route, use the route id (brokerage ID)
        if (isBrokerageOffice) {
            return id || "";
        }
        // If it's a brokerage agent route and user has selected an agent, use agent ID
        if (isBrokerageAgent && selectedAgentOption?.value) {
            return selectedAgentOption.value;
        }
        // For regular agent routes (not brokerage), use the agent ID from route
        if (agentIdFromRoute) {
            return agentIdFromRoute;
        }
        // Fallback: return empty string if nothing is selected yet
        return "";
    }, [isBrokerageOffice, isBrokerageAgent, id, selectedAgentOption, agentIdFromRoute]);
    
    // ✅ Use the brand-category selector hook with selected agent ID
    const {
        selectedBrand,
        setSelectedBrand,
        selectedCategories,
        setSelectedCategories,
        brandOptions: brandOptionsFromHook,
        filteredCategories: categoryOptionsFromHook,
        loadingBrands,
        loadingCategories,
    } = useBrandCategorySelector(agentIdForBrandCategory, token || "");
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    // console.log("previewFiles", previewFiles);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    // console.log("uploadedFileMeta", uploadedFileMeta);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
/* LEGACY (unused): warehouse hierarchy option cache
    const [locationOptions, setLocationOptions] = useState<{
        [warehouseId: string]: {
            zones: any[];
            racks: any[];
            bins: any[];
        };
    }>({});
*/


    const [warehouseData, setWarehouseData] = useState<
        {
            warehouse_id: string;
            warehouse_name: string;
            zone_id: string;
            zone_name: string;
            rack_id: string;
            rack_name: string;
            bin_id: string;
            bin_name: string;
            available: number;
            on_hand: number;
            reserved: number;
        }[]
    >([]);

    const [selectedLocations, setSelectedLocations] = useState<Array<{
        quantity: number;
        warehouse_id: string; warehouse_name: string;
        zone_id: string; zone_name: string;
        rack_id: string; rack_name: string;
        bin_id: string; bin_name: string;
    }>>([]);
    const [selectedTotal, setSelectedTotal] = useState(0);


    const [warehouseErrors, setWarehouseErrors] = useState<{
        zone: boolean;
        rack: boolean;
        bin: boolean;
    }[]>([]);

    useEffect(() => {
        setWarehouseErrors(warehouseData.map(() => ({ zone: false, rack: false, bin: false })));
    }, [warehouseData]);

    // Fetch parent hierarchy for signageteammember login (parent SIGNAGE_FIRM id for supplier_id)
    useEffect(() => {
        const fetchParentHierarchy = async () => {
            if (!isSignageTeammember || !token) return;

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) return;

                const data = await response.json();
                const signageFirm = data.parent_chain?.find(
                    (parent: any) => parent.license_type === "SIGNAGE_FIRM"
                );

                if (signageFirm) {
                    setParentSignageId(signageFirm.id || signageFirm._id || null);
                    setParentSignageName(signageFirm.name || "");
                }
            } catch (error) {
                console.error("Error fetching parent hierarchy:", error);
            }
        };

        fetchParentHierarchy();
    }, [isSignageTeammember, token]);

    // Handle Product Details quantity change - distribute to warehouse table
    const handleQuantityChange = (value: string) => {
        const newQty = Number(value) || 0;
        formik.setFieldValue("qty", newQty);

        // Distribute the new quantity across warehouses proportionally
        const currentTotal = warehouseData.reduce((sum, wh) => sum + Number(wh.available || 0), 0);

        if (currentTotal === 0) {
            // If no current distribution, put all quantity in first warehouse
            setWarehouseData(prev =>
                prev.map((wh, index) =>
                    index === 0
                        ? { ...wh, available: newQty, on_hand: newQty }
                        : { ...wh, available: 0, on_hand: 0 }
                )
            );
        } else {
            // Distribute proportionally
            setWarehouseData(prev =>
                prev.map(wh => {
                    const proportion = Number(wh.available || 0) / currentTotal;
                    const newAvailable = Math.round(newQty * proportion);
                    return {
                        ...wh,
                        available: newAvailable,
                        on_hand: newAvailable
                    };
                })
            );
        }
    };

    const validateWarehouseData = (): boolean => {
        let hasErrors = false;
        const errors = warehouseData.map(() => ({ zone: false, rack: false, bin: false }));

        warehouseData.forEach((wh, i) => {
            if (wh.available > 0) {
                if (!wh.zone_id) {
                    errors[i].zone = true;
                    hasErrors = true;
                }
                if (!wh.rack_id) {
                    errors[i].rack = true;
                    hasErrors = true;
                }
                if (!wh.bin_id) {
                    errors[i].bin = true;
                    hasErrors = true;
                }
            }
        });

        setWarehouseErrors(errors);
        return !hasErrors;
    };








/* LEGACY (unused): POST /warehouses/hierarchy loaders and cascading handlers
    useEffect(() => {
        async function loadZones() {
            let opts: typeof locationOptions = {};
            for (const wh of warehouses) {
                try {
                    const data = await fetchHierarchy({ warehouse_id: wh._id });
                    opts[wh._id] = { zones: data.zones || [], racks: [], bins: [] };
                } catch {
                    opts[wh._id] = { zones: [], racks: [], bins: [] };
                }
            }
            setLocationOptions(opts);
        }
        if (warehouses?.length) loadZones();
    }, [warehouses]);

    useEffect(() => {
        if (warehouses?.length === 0) return;

        async function loadAllLocationOptions() {
            const opts: Record<string, { zones: any[]; racks: any[]; bins: any[] }> = {};

            // For tracking which racks/bins we've already fetched
            const fetchedZoneRackPairs = new Set<string>();
            const fetchedRackBinPairs = new Set<string>();

            for (const wh of warehouses) {
                opts[wh._id] = { zones: [], racks: [], bins: [] };

                // 1. Fetch zones for warehouse
                try {
                    const zoneData = await fetchHierarchy({ warehouse_id: wh._id });
                    opts[wh._id].zones = zoneData.zones || [];
                } catch {
                    opts[wh._id].zones = [];
                }

                // 2. Find warehouseData matching this warehouse from your selected combination
                const whData = warehouseData.find(wd => wd.warehouse_id === wh._id);

                if (whData?.zone_id) {
                    // 3. Fetch racks for zone if not already fetched
                    const zoneKey = `${wh._id}_${whData.zone_id}`;
                    if (!fetchedZoneRackPairs.has(zoneKey)) {
                        try {
                            const racksRes = await fetchHierarchy({ zone_id: whData.zone_id });
                            opts[wh._id].racks = racksRes.racks || [];
                        } catch {
                            opts[wh._id].racks = [];
                        }
                        fetchedZoneRackPairs.add(zoneKey);
                    }

                    // 4. Fetch bins for rack if rack_id present and not already fetched
                    if (whData?.rack_id) {
                        const rackKey = `${wh._id}_${whData.rack_id}`;
                        if (!fetchedRackBinPairs.has(rackKey)) {
                            try {
                                const binsRes = await fetchHierarchy({ rack_id: whData.rack_id });
                                opts[wh._id].bins = binsRes.bins || [];
                            } catch {
                                opts[wh._id].bins = [];
                            }
                            fetchedRackBinPairs.add(rackKey);
                        }
                    }
                }
            }

            setLocationOptions(opts);
        }

        loadAllLocationOptions();
        // console.log("warehouseData :", warehouseData)
    }, [warehouses, warehouseData]);




    async function fetchHierarchy(payload: object) {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/hierarchy`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to fetch hierarchy");
        return res.json();
    }
    const handleWarehouseFieldChange = (index: number, value: string) => {
        const updated = [...warehouseData];
        const numVal = Number(value);
        updated[index].available = numVal;
        updated[index].on_hand = numVal; // keep synced if needed
        setWarehouseData(updated);

        // Clear errors on change
        setWarehouseErrors((prev) => {
            const copy = [...prev];
            copy[index] = { zone: false, rack: false, bin: false };
            return copy;
        });
    };

    const handleZoneChange = async (index: number, zone_id: string, zone_name: string) => {
        const updated = [...warehouseData];
        updated[index] = {
            ...updated[index],
            zone_id,
            zone_name,
            rack_id: "",
            rack_name: "",
            bin_id: "",
            bin_name: "",
        };
        setWarehouseData(updated);

        // Fetch racks for this zone and update locationOptions
        try {
            const data = await fetchHierarchy({ zone_id });
            setLocationOptions((prev) => ({
                ...prev,
                [updated[index].warehouse_id]: {
                    ...prev[updated[index].warehouse_id],
                    racks: data.racks || [],
                    bins: [],
                },
            }));
        } catch (e) {
            console.error("Failed to load racks for zone", e);
        }

        // Clear zone error
        setWarehouseErrors((prev) => {
            const copy = [...prev];
            copy[index].zone = false;
            return copy;
        });
    };

    const handleRackChange = async (index: number, rack_id: string, rack_name: string) => {
        const updated = [...warehouseData];
        updated[index] = {
            ...updated[index],
            rack_id,
            rack_name,
            bin_id: "",
            bin_name: "",
        };
        setWarehouseData(updated);

        // Fetch bins for this rack and update locationOptions
        try {
            const data = await fetchHierarchy({ rack_id });
            setLocationOptions((prev) => ({
                ...prev,
                [updated[index].warehouse_id]: {
                    ...prev[updated[index].warehouse_id],
                    bins: data.bins || [],
                },
            }));
        } catch (e) {
            console.error("Failed to load bins for rack", e);
        }

        // Clear rack error
        setWarehouseErrors((prev) => {
            const copy = [...prev];
            copy[index].rack = false;
            return copy;
        });
    };

    const handleBinChange = (index: number, bin_id: string, bin_name: string) => {
        const updated = [...warehouseData];
        updated[index] = {
            ...updated[index],
            bin_id,
            bin_name,
        };
        setWarehouseData(updated);

        // Clear bin error
        setWarehouseErrors((prev) => {
            const copy = [...prev];
            copy[index].bin = false;
            return copy;
        });
    };




*/

    // ✅ Brands and categories are now loaded via useBrandCategorySelector hook above
    // No separate useEffects needed!

    


    function getInstallerById(installerId: string) {
        // console.log("Looking up installer for ID:", installerId);
        return installers?.find((installer) => installer.supplier_id === installerId) || null;
    }

    // Helper functions for agent selection
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");
    
    const mapAgentToOption = (row: any): AgentOption => ({
        value: normalizeId(row),
        label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
        firstName: row?.firstName || "",
        lastName: row?.lastName || "",
        email: row?.email || "",
        phone: row?.phone1 || "",
    });

    const PAGE_SIZE = 10;
    
    // Load agent options for brokerage agents
    const loadAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            
            if (!brokerageId || !token) {
                return { options: [], hasMore: false, additional: { page } };
            }

            try {
                const params = new URLSearchParams({
                    licenseType: "BROKERAGE_AGENT",
                    brokerageFirmID: brokerageId,
                    page: String(page),
                    limit: String(PAGE_SIZE)
                });
                const search = (inputValue || "").trim();
                if (search) params.set("search", search);

                const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;

                const response = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    console.error("Failed to fetch agents:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const responseData = await response.json();
                const results = Array.isArray(responseData) ? responseData : responseData.results || [];
                const hasMore = Array.isArray(responseData)
                    ? results.length === PAGE_SIZE
                    : responseData.pagination?.more || false;

                const options: AgentOption[] = results.map(mapAgentToOption);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (error) {
                console.error("Error loading agent options:", error);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [brokerageId, token]
    );

    // Fetch offices for brokerage office dropdown
    useEffect(() => {
        const fetchOffices = async () => {
            if (!isBrokerageOffice || !brokerageId || !token) return;
            
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/office/${brokerageId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch offices");
                }

                const data = await response.json();
                setOffices(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching offices:", err);
                setError(err instanceof Error ? err.message : "Failed to fetch offices");
            }
        };

        fetchOffices();
    }, [isBrokerageOffice, brokerageId, token]);

    // Get the agent ID or office ID to use for API calls
    const getEntityId = useCallback(() => {
        if (isBrokerageOffice) {
            return selectedOfficeOption?.value || "";
        }
        return selectedAgentOption?.value || agentIdFromRoute || "";
    }, [selectedAgentOption, selectedOfficeOption, agentIdFromRoute, isBrokerageOffice]);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/warehouses`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                setWarehouses(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchWarehouses();
    }, [token]);

    // const uploadAllSelectedImages = async () => {
    //   const uploads = await Promise.all(
    //     previewFiles.map(async ({ file }) => ({ alt_text: file.name, url: await uploadFile(file) }))
    //   );
    //   const all = [...(uploadedFileMeta || []), ...uploads];
    //   // de-dupe by filename
    //   const uniq = all.filter((f, i, a) => i === a.findIndex(x => x.alt_text === f.alt_text));
    //   setUploadedFileMeta(uniq);
    //   setPreviewFiles([]);
    //   return uniq;
    // };


    // helper: normalize & dedupe by URL
    const sourceUrls = React.useMemo(() => {
        const fromUploads =
            Array.isArray(uploadedFileMeta)
                ? uploadedFileMeta.map((m: any) => m?.url).filter(Boolean)
                : [];

        const merged = [...fromUploads];

        // ✅ De-dupe without iterating a Set (no downlevelIteration needed)
        const seen = new Set<string>();
        const unique: string[] = [];
        for (let i = 0; i < merged.length; i++) {
            const v = String(merged[i] ?? "");
            if (!v) continue;
            if (!seen.has(v)) {
                seen.add(v);
                unique.push(v);
            }
        }
        return unique;
    }, [uploadedFileMeta]);

    const imageUrlMap = useVariantImageUrlMap(sourceUrls, token);


    async function uploadProductImagesToCloudDrive(): Promise<string[]> {
        const tok = (token || "").trim() || null;
        // use the files you already collected in previewFiles
        const files = (previewFiles || []).map(p => p.file);
        if (!files.length) return [];

        const ids = await uploadFilesAndGetIds(files, tok);
        setPreviewFiles([]);           // clear after successful upload
        return ids;                    // send back to onSubmit
    }


    const formik = useFormik({
        enableReinitialize: false,
        initialValues: {
            name: "",
            description: "",
            sku: "",
            brand: "",
            category: "",
            item_condition: "",
            length: 0,
            height: 0,
            width: 0,
            weight: 0,
            qty: 0,
            threshold_qty: 0,
            installerId: "",
            notes: "",
            image: null,
            imageUrl: null,
            statuss: "Active",
            is_rotational: "no",
            linked_agent: "",
            installer_notes: "",
            linked_installer: "",
            installer_status: "",
            agentSelection: "",
            officeSelection: "",
        },
        validationSchema: Yup.object({
            name: nameValidation,
            sku: Yup.string().notRequired(),
            statuss: statusValidation,
            agentSelection: isBrokerageAgent 
                ? Yup.string().required("Agent selection is required")
                : Yup.string().notRequired(),
            officeSelection: isBrokerageOffice 
                ? Yup.string().required("Office selection is required")
                : Yup.string().notRequired(),
        }),
        onSubmit: async (values) => {
            // console.log("Submitting form...", values);
            setFormSubmitting(true);
            
            // Validate warehouse data if locations are selected
            if (selectedLocations.length > 0) {
                const hasInvalidLocation = selectedLocations.some(loc => 
                    !loc.warehouse_id || !loc.zone_id || !loc.rack_id || !loc.bin_id
                );
                if (hasInvalidLocation) {
                    messageHandler.error("agent", "create", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation.warehouseDetailsRequired));
                    setFormSubmitting(false);
                    return;
                }
            }

            // 1) Use the NEW locations from the shared UI
            const filteredWarehouses = selectedLocations.map(loc => ({
                warehouse_id: loc.warehouse_id,
                warehouse_name: loc.warehouse_name,
                zone_id: loc.zone_id,
                zone_name: loc.zone_name,
                rack_id: loc.rack_id,
                rack_name: loc.rack_name,
                bin_id: loc.bin_id,
                bin_name: loc.bin_name,
                available: Number(loc.quantity || 0),
                on_hand: Number(loc.quantity || 0),
                reserved: 0,
            }));

            // 2) Sum total
            const totalAvailable = filteredWarehouses.reduce((sum, wh) => sum + Number(wh.available || 0), 0);

            try {
                const newImageIds = await uploadProductImagesToCloudDrive();

                // 2) Convert *existing* (kept) images from state as-is
                const kept: Media[] = (uploadedFileMeta ?? []).map((m: any) => ({
                    url: m?.url,
                    alt_text: m?.alt_text || "",
                    default: !!m?.default,
                }));

                // 3) Convert *new* uploads into Media[]
                const newMedia: Media[] = newImageIds.map((id, idx) => ({
                    url: id,
                    alt_text: (previewFiles?.[idx]?.file?.name as string) || "",
                    default: false,
                }));

                // 4) Merge and ensure exactly one default (if none, make the first one default)
                let images: Media[] = [...kept, ...newMedia];
                if (!images.some((m) => m.default) && images.length > 0) {
                    images = images.map((m, i) => ({ ...m, default: i === 0 }));
                }

                try {
                    if (pendingDeleteIds.length > 0) {
                        await hardDeletePresignedFiles(pendingDeleteIds, token || null);
                        setPendingDeleteIds([]); // clear
                    }
                } catch (delErr: any) {
                    console.error("Image hard delete (batch) failed:", delErr);
                    // Don't block success — just inform
                    messageHandler.error("agent", "create", new Error(delErr?.message || AGENTOFFICEPRODUCTADDEDIT_MESSAGES.image.deleteFailed));
                }

                // Find brand and category names from IDs
                const selectedBrandOption = brandOptionsFromHook.find(b => b.value === selectedBrand);
                const selectedCategoryOption = categoryOptionsFromHook.find(c => c.value === values.category);

                // CREATE NEW PRODUCT - POST request
                const entityId = getEntityId();
                if (!entityId) {
                    const errorMsg = isBrokerageOffice 
                        ? AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation.officeSelectionRequired 
                        : AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation.agentIdRequired;
                    messageHandler.error("agent", "create", new Error(errorMsg));
                    setFormSubmitting(false);
                    return;
                }

                // Get selected office details for office products
                let officeData: any = null;
                if (isBrokerageOffice && selectedOfficeOption?.value) {
                    const selectedOffice = offices.find((office: any) => office._id === selectedOfficeOption.value);
                    if (selectedOffice) {
                        const officeAddress = selectedOffice.officeAddress?.[0] || {};
                        officeData = {
                            id: selectedOffice._id,
                            officeName: selectedOffice.officeName || "",
                            officeType: selectedOffice.officeType || "",
                            officeAddress: {
                                addressLine1: officeAddress.addressLine1 || "",
                                addressLine2: officeAddress.addressLine2 || "",
                                city: officeAddress.city || "",
                                state: officeAddress.state || "",
                                country: officeAddress.country || "",
                                zip: officeAddress.zip || ""
                            }
                        };
                    }
                }

                const createBody: any = {
                    inventory_type: isBrokerageOffice ? "office" : "agent",
                    reference_product_id: "",
                };

                // Add office object only for office products
                if (isBrokerageOffice && officeData) {
                    createBody.office = officeData;
                }

                const brandId = String(selectedBrand || "").trim();
                const brandName = String(selectedBrandOption?.label || "").trim();
                const categoryId = String(values.category || "").trim();
                const categoryName = String(selectedCategoryOption?.label || "").trim();
                const isMeaningfulId = (id: string) => Boolean(id) && !/^0+$/.test(id);

                createBody.product = {
                        name: values.name,
                        description: values.description || "",
                        product_type: "simple",
                        short_description: values.description || "",
                        sku: values.sku,
                        ...(isMeaningfulId(brandId)
                            ? { brand: brandName, brand_id: brandId }
                            : {}),
                        ...(isMeaningfulId(categoryId)
                            ? { category: categoryName, category_id: categoryId }
                            : {}),
                        item_condition: values.item_condition || "",
                        condition_note: "",
                        purchase_price: 0,
                        retail_price: 0,
                        images: images,
                        package_dimensions: {
                            length: Number(values.length) || 0,
                            width: Number(values.width) || 0,
                            height: Number(values.height) || 0,
                            weight: Number(values.weight) || 0,
                            weight_type: "g"
                        },
                        threshold_qty: Number(values.threshold_qty) || 0,
                        attributes: {},
                        barcode: "",
                        min_qty_for_sale: 1,
                        variants: [
                            {
                                variant_name: values.name,
                                sku: values.sku,
                                description: values.description || "",
                                images: images,
                                attributes: {},
                                purchase_price: 0,
                                retail_price: 0,
                                package_dimensions: {
                                    length: Number(values.length) || 0,
                                    height: Number(values.height) || 0,
                                    width: Number(values.width) || 0,
                                    weight: Number(values.weight) || 0,
                                    weight_type: "lb"
                                },
                                threshold_qty: Number(values.threshold_qty) || 0,
                                inventory: [
                                    {
                                        on_hand: totalAvailable || Number(values.qty) || 0,
                                        available: totalAvailable || Number(values.qty) || 0,
                                        reserved: 0
                                    }
                                ],
                                barcode: "",
                                is_rotational: values.is_rotational === "yes"
                            }
                        ],
                        status: values.statuss || "Active",
                        is_rotational: values.is_rotational === "yes",
                        rotational_status: "",
                        linked_mode: "none",
                        locked_status: "unlocked",
                        linked_installer: null,
                        is_taxable: null,
                        tax_rule: null,
                        installer: "",
                        installer_notes: "",
                        min_qty: 1
                    };

                const endpoint = isBrokerageOffice 
                    ? `${process.env.REACT_APP_API_URL}/office/${entityId}/inventory`
                    : `${process.env.REACT_APP_API_URL}/agent/${entityId}/inventory`;

                const response = await fetch(
                    endpoint,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(createBody),
                    }
                );

                const result: any = await response.json();
                if (!response.ok || result?.error) {
                    const message = result?.error || result?.message || AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.createFailed;
                    console.error("API Error:", message);
                    messageHandler.product.error("create", new Error(message));
                    throw new Error(message);
                }

                // Extract product_id from response
                const productId = result?.product_id;
                if (!productId) {
                    messageHandler.product.error("create", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.productIdNotFound));
                    throw new Error("Product ID not found in response");
                }

                // Step 2: Link supplier + accept stock in one call
                const locationMap = new Map<string, {
                    quantity: number;
                    warehouse_id: string;
                    warehouse_name: string;
                    zone_id: string;
                    zone_name: string;
                    rack_id: string;
                    rack_name: string;
                    bin_id: string;
                    bin_name: string;
                }>();

                selectedLocations.forEach(loc => {
                    const key = `${loc.warehouse_id}_${loc.zone_id}_${loc.rack_id}_${loc.bin_id}`;
                    if (locationMap.has(key)) {
                        const existing = locationMap.get(key)!;
                        existing.quantity += loc.quantity;
                    } else {
                        locationMap.set(key, {
                            quantity: loc.quantity,
                            warehouse_id: loc.warehouse_id,
                            warehouse_name: loc.warehouse_name,
                            zone_id: loc.zone_id,
                            zone_name: loc.zone_name,
                            rack_id: loc.rack_id,
                            rack_name: loc.rack_name,
                            bin_id: loc.bin_id,
                            bin_name: loc.bin_name,
                        });
                    }
                });

                const locationArray = Array.from(locationMap.values()).map(loc => ({
                    quantity: loc.quantity,
                    warehouse_id: loc.warehouse_id,
                    warehouse_name: loc.warehouse_name,
                    zone_id: loc.zone_id || "",
                    zone_name: loc.zone_name || "",
                    rack_id: loc.rack_id || "",
                    rack_name: loc.rack_name || "",
                    bin_id: loc.bin_id || "",
                    bin_name: loc.bin_name || "",
                }));

                const totalQuantity = locationArray.reduce((sum, loc) => sum + loc.quantity, 0);

                const linkAndAcceptBody = {
                    supplier_id: effectiveSupplierId,
                    supplier_name: effectiveSupplierName,
                    location_type: "none",
                    drop_off_date: new Date().toISOString(),
                    notes: "",
                    is_rotational: values.is_rotational === "yes",
                    storage_locations: [
                        {
                            quantity: totalQuantity,
                            product_location_status: "none",
                            current_status: "with_supplier",
                            location: locationArray,
                        },
                    ],
                };

                const linkAndAcceptEndpoint = isBrokerageOffice
                    ? `${process.env.REACT_APP_API_URL}/linked-installer/office/${entityId}/product/${productId}/link-and-accept`
                    : `${process.env.REACT_APP_API_URL}/linked-installer/agent/${entityId}/product/${productId}/link-and-accept`;

                const linkAndAcceptResponse = await fetch(linkAndAcceptEndpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(linkAndAcceptBody),
                });

                const linkAndAcceptResult: any = await linkAndAcceptResponse.json();
                if (!linkAndAcceptResponse.ok || linkAndAcceptResult?.error) {
                    const message =
                        linkAndAcceptResult?.error ||
                        linkAndAcceptResult?.message ||
                        AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.linkInstallerFailed;
                    console.error("API Error:", message);
                    messageHandler.product.error("create", new Error(message));
                    throw new Error(message);
                }

                // Create + link-and-accept successful
                messageHandler.product.success("create");
                navigate(-1);
            } catch (err) {
                console.error(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setFormSubmitting(false);
            }
        },
    });

    // Sync Product Details quantity with warehouse table total
    // DISABLED: This was preventing manual quantity changes
    // useEffect(() => {
    //     const totalAvailable = warehouseData.reduce((sum, wh) => sum + Number(wh.available || 0), 0);
    //     if (formik.values.qty !== totalAvailable) {
    //         formik.setFieldValue("qty", totalAvailable);
    //     }
    // }, [warehouseData, formik]);

    // Pass this as the "saving" flag to the shared UI
    const saving = formSubmitting;


/* LEGACY (unused): warehouse table columns (replaced by Select2 location rows)
    const toSO = (list: any[] = []) =>
        (Array.isArray(list) ? list : []).map((x) => ({ value: String(x._id ?? x.id ?? ""), label: String(x.label ?? x.name ?? "") }));

    // Local input component to prevent cursor jumping
    const QuantityInput = ({ value, onUpdate }: { value: number; onUpdate: (n: number) => void }) => {
        const [localValue, setLocalValue] = React.useState(String(value ?? 0));

        React.useEffect(() => {
            setLocalValue(String(value ?? 0));
        }, [value]);

        return (
            <input
                type="text"
                className="form-control text-center"
                value={localValue}
                placeholder="0"
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() => {
                    const n = Number(localValue) || 0;
                    onUpdate(n);
                }}
                style={{ minWidth: '80px' }}
            />
        );
    };

    const columns: ColumnDef<WHRow>[] = [
        {
            accessorKey: "warehouse_name",
            header: "Warehouse",
            cell: ({ row }) => <div className="">{row.original.warehouse_name}</div>,
        },
        {
            id: "available",
            header: "Quantity",
            cell: ({ row }) => {
                const wh = row.original;
                return (
                    <div className="">
                        <QuantityInput
                            value={wh.available}
                            onUpdate={(n) => {
                                setWarehouseData((prev) =>
                                    prev.map((r, i) =>
                                        i === row.index
                                            ? {
                                                ...r,
                                                available: n,
                                                on_hand: n,
                                                // clear dependent selections when qty is zero
                                                zone_id: n === 0 ? "" : r.zone_id,
                                                zone_name: n === 0 ? "" : r.zone_name,
                                                rack_id: n === 0 ? "" : r.rack_id,
                                                rack_name: n === 0 ? "" : r.rack_name,
                                                bin_id: n === 0 ? "" : r.bin_id,
                                                bin_name: n === 0 ? "" : r.bin_name,
                                            }
                                            : r
                                    )
                                );
                            }}
                        />
                    </div>
                );
            },
        },
        {
            id: "zone",
            header: "Zone",
            cell: ({ row }) => {
                const wh = row.original;
                const disabled = !(wh.available > 0);
                const opts = toSO(locationOptions[wh.warehouse_id]?.zones || []);
                return (
                    <UIReactSelect
                        name={`warehouses.${row.index}.zone_id`}
                        placeholder="Select Zone"
                        value={wh.zone_id}
                        disabled={disabled}
                        options={opts}
                        onChange={async (zone_id) => {
                            const z = (locationOptions[wh.warehouse_id]?.zones || []).find((z) => String(z._id) === String(zone_id));
                            // update row
                            setWarehouseData((prev) =>
                                prev.map((r, i) =>
                                    i === row.index
                                        ? { ...r, zone_id: String(zone_id), zone_name: z?.label || "", rack_id: "", rack_name: "", bin_id: "", bin_name: "" }
                                        : r
                                )
                            );
                            // fetch racks for the new zone
                            try {
                                const data = await fetchHierarchy({ zone_id });
                                setLocationOptions((prev) => ({
                                    ...prev,
                                    [wh.warehouse_id]: { ...(prev[wh.warehouse_id] || {}), racks: data.racks || [], bins: [] },
                                }));
                            } catch { }
                        }}
                    />
                );
            },
        },
        {
            id: "rack",
            header: "Rack",
            cell: ({ row }) => {
                const wh = row.original;
                const disabled = !(wh.available > 0) || !wh.zone_id;
                const opts = toSO(locationOptions[wh.warehouse_id]?.racks || []);
                return (
                    <UIReactSelect
                        name={`warehouses.${row.index}.rack_id`}
                        placeholder="Select Rack"
                        value={wh.rack_id}
                        disabled={disabled}
                        options={opts}
                        onChange={async (rack_id) => {
                            const r = (locationOptions[wh.warehouse_id]?.racks || []).find((r) => String(r._id) === String(rack_id));
                            setWarehouseData((prev) =>
                                prev.map((x, i) => (i === row.index ? { ...x, rack_id: String(rack_id), rack_name: r?.label || "", bin_id: "", bin_name: "" } : x))
                            );
                            try {
                                const data = await fetchHierarchy({ rack_id });
                                setLocationOptions((prev) => ({
                                    ...prev,
                                    [wh.warehouse_id]: { ...(prev[wh.warehouse_id] || {}), bins: data.bins || [] },
                                }));
                            } catch { }
                        }}
                    />
                );
            },
        },
        {
            id: "bin",
            header: "Bin Location",
            cell: ({ row }) => {
                const wh = row.original;
                const disabled = !(wh.available > 0) || !wh.rack_id;
                const opts = toSO(locationOptions[wh.warehouse_id]?.bins || []);
                return (
                    <UIReactSelect
                        name={`warehouses.${row.index}.bin_id`}
                        placeholder="Select Bin"
                        value={wh.bin_id}
                        disabled={disabled}
                        options={opts}
                        onChange={(bin_id) => {
                            const b = (locationOptions[wh.warehouse_id]?.bins || []).find((b) => String(b._id) === String(bin_id));
                            setWarehouseData((prev) =>
                                prev.map((x, i) => (i === row.index ? { ...x, bin_id: String(bin_id), bin_name: b?.label || "" } : x))
                            );
                        }}
                    />
                );
            },
        },
    ];
    const table: Table<WHRow> = useReactTable<WHRow>({
        data: warehouseData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    });

*/

    // ✅ Brand and category options from hook
    const brandOptionsMemo = useMemo(() => {
        return brandOptionsFromHook;
    }, [brandOptionsFromHook]);

    // ✅ Category options are already filtered by selected brand (empty if no brand selected)
    const categoryOptionsMemo = useMemo(() => {
        // ✅ Double-check: only return categories if brand is selected
        return selectedBrand ? categoryOptionsFromHook : [];
    }, [categoryOptionsFromHook, selectedBrand]);

    // Office options for brokerage office dropdown
    const officeOptionsMemo = useMemo(() => {
        return offices.map((office: any) => ({
            value: office._id || "",
            label: office.officeName || "",
        }));
    }, [offices]);

    // Sync selectedOfficeOption with formik value
    useEffect(() => {
        if (isBrokerageOffice && formik.values.officeSelection) {
            const selected = officeOptionsMemo.find(opt => opt.value === formik.values.officeSelection);
            if (selected && (!selectedOfficeOption || selectedOfficeOption.value !== selected.value)) {
                setSelectedOfficeOption(selected);
            }
        }
    }, [formik.values.officeSelection, officeOptionsMemo, isBrokerageOffice, selectedOfficeOption]);

    // --- preview/add/remove handlers (simple & safe) ---
    const handleAddFiles = (files: File[]) => {
        if (!files?.length) return;
        const fresh = files.map(file => ({ file, url: URL.createObjectURL(file) }));
        setPreviewFiles(prev => [...prev, ...fresh]);
    };

    const handleRemovePreview = (idx: number) => {
        setPreviewFiles(prev => {
            const copy = [...prev];
            // revoke old blob URL to avoid leaks
            try { URL.revokeObjectURL(copy[idx]?.url); } catch { }
            copy.splice(idx, 1);
            return copy;
        });
    };

    const handleRemoveUploaded = async (idx: number) => {
        setUploadedFileMeta(prev => {
            const copy = [...prev];
            const [removed] = copy.splice(idx, 1);
            // fire-and-forget delete if your backend uses presigned uploads
            if (removed?.url) hardDeletePresignedFiles([removed.url], token!).catch(() => { });
            return copy;
        });
    };

    // --- pass a strict string|null resolver (never undefined) ---
    const resolveImageSrc = useCallback(
        (url: string): string | null => {
            if (!url) return null;
            const resolved = getImageSrc(url, imageUrlMap);
            if (resolved) return resolved;
            if (url.startsWith("http")) return url;
            return buildLegacyUrl(url) ?? null;
        },
        [imageUrlMap]
    );


    // ✅ Refs to prevent circular updates and track previous values
    const isSyncingFromFormik = useRef(false);
    const isSyncingFromHook = useRef(false);
    const prevFormikBrand = useRef<string>("");
    const prevFormikCategory = useRef<string>("");
    const prevSelectedBrand = useRef<string>("");
    const prevSelectedCategory = useRef<string>("");

    // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
    useEffect(() => {
        if (isSyncingFromHook.current) {
            isSyncingFromHook.current = false;
            return;
        }

        const brandValue = formik.values.brand || "";
        if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
            prevFormikBrand.current = brandValue;
            // console.log("🔄 [Formik→Hook] Brand changed:", brandValue);
            isSyncingFromFormik.current = true;
            setSelectedBrand(brandValue);
            setSelectedCategories([]); // Clear categories when brand changes
            setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
        }
    }, [formik.values.brand, selectedBrand]);

    // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
    useEffect(() => {
        if (isSyncingFromHook.current) {
            isSyncingFromHook.current = false;
            return;
        }

        const categoryValue = formik.values.category || "";
        const currentFirst = selectedCategories[0] || "";
        if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
            prevFormikCategory.current = categoryValue;
            // console.log("🔄 [Formik→Hook] Category changed:", categoryValue);
            isSyncingFromFormik.current = true;
            setSelectedCategories([categoryValue]);
            setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
        }
    }, [formik.values.category, selectedCategories]);

    // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
    useEffect(() => {
        if (isSyncingFromFormik.current) {
            return;
        }

        if (selectedBrand && selectedBrand !== prevSelectedBrand.current && formik.values.brand !== selectedBrand) {
            prevSelectedBrand.current = selectedBrand;
            // console.log("🔄 [Hook→Formik] Brand changed:", selectedBrand);
            isSyncingFromHook.current = true;
            formik.setFieldValue("brand", selectedBrand, false);
            setTimeout(() => { isSyncingFromHook.current = false; }, 0);
        }
    }, [selectedBrand, formik.values.brand]);

    // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
    useEffect(() => {
        if (isSyncingFromFormik.current) {
            return;
        }

        const firstCategory = selectedCategories[0] || "";
        if (firstCategory && firstCategory !== prevSelectedCategory.current && formik.values.category !== firstCategory) {
            prevSelectedCategory.current = firstCategory;
            // console.log("🔄 [Hook→Formik] Category changed:", firstCategory);
            isSyncingFromHook.current = true;
            formik.setFieldValue("category", firstCategory, false);
            setTimeout(() => { isSyncingFromHook.current = false; }, 0);
        } else if (!firstCategory && formik.values.category) {
            // Clear category if hook has no selection
            if (prevSelectedCategory.current !== "") {
                prevSelectedCategory.current = "";
                // console.log("🔄 [Hook→Formik] Clearing category");
                isSyncingFromHook.current = true;
                formik.setFieldValue("category", "", false);
                setTimeout(() => { isSyncingFromHook.current = false; }, 0);
            }
        }
    }, [selectedCategories, formik.values.category]);

    return (
        <SharedEditProductUI
            title="Create Product"
            formik={formik}
            brandOptions={brandOptionsMemo}
            categoryOptions={categoryOptionsMemo}
            conditionOptions={conditionOptions}
            statusOptions={statusOptions}
            saving={saving}
            onBack={() => navigate(-1)}
            onSubmit={() => formik.handleSubmit()}
            previewFiles={previewFiles}
            uploadedFiles={uploadedFileMeta}
            onAddFiles={handleAddFiles}
            onRemovePreview={handleRemovePreview}
            onRemoveUploaded={handleRemoveUploaded}
            baseUrl={process.env.REACT_APP_BASE_URL}
            // warehouseTable={table}  // LEGACY (unused)
            mobileCardIds={{ idColId: "warehouse_name", titleColId: "warehouse_name", actionColId: "actions" }}
            onQuantityChange={handleQuantityChange}
            disableQuantityField={true}
            requireSku={false}
            resolveSrc={resolveImageSrc}
            onLocationsChange={(locs, total) => {
                setSelectedLocations(locs);
                setSelectedTotal(total);
            }}
            showAgentDropdown={isBrokerageAgent}
            loadAgentOptions={isBrokerageAgent ? loadAgentOptions : undefined}
            selectedAgentOption={selectedAgentOption}
            onAgentChange={(agent) => {
                setSelectedAgentOption(agent);
            }}
            showOfficeDropdown={isBrokerageOffice}
            officeOptions={officeOptionsMemo}
            selectedOfficeOption={selectedOfficeOption}
            onOfficeChange={(office) => {
                setSelectedOfficeOption(office);
            }}
            type="add"
            ownerNodeId={effectiveSupplierId}
        />
    );


};

export default AddAgentOfficeProduct;
